import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Plus, Loader2, Pencil, Trash2, Search } from "lucide-react";
import {
  fetchContent,
  createContent,
  updateContent,
  deleteContent,
} from "../../redux/slices/contentSlice";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../components/ui/use-toast";

const emptyForm = () => ({
  key: "",
  category: "",
  type: "text",
  is_published: true,
  translationsJson: '{\n  "en": ""\n}',
});

function ContentPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { content, loading } = useSelector((s) => s.content);

  const [categoryFilter, setCategoryFilter] = useState("");
  const [keyFilter, setKeyFilter] = useState("");
  const [highlightKey, setHighlightKey] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const openedFromUrl = useRef(false);

  useEffect(() => {
    const keyFromUrl = searchParams.get("key");
    if (keyFromUrl) {
      setKeyFilter(keyFromUrl);
      setHighlightKey(keyFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(
      fetchContent(
        categoryFilter.trim() ? { category: categoryFilter.trim() } : {}
      )
    );
  }, [dispatch, categoryFilter]);

  const filteredContent = useMemo(() => {
    const q = keyFilter.trim().toLowerCase();
    if (!q) return content;
    return content.filter((row) => {
      const key = (row.key || "").toLowerCase();
      const category = (row.category || "").toLowerCase();
      return key.includes(q) || category.includes(q);
    });
  }, [content, keyFilter]);

  const clearKeyFilter = () => {
    setKeyFilter("");
    setHighlightKey("");
    if (searchParams.get("key")) {
      const next = new URLSearchParams(searchParams);
      next.delete("key");
      setSearchParams(next, { replace: true });
    }
  };

  const openCreate = () => {
    setEditingKey(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingKey(row.key);
    setForm({
      key: row.key,
      category: row.category || "",
      type: row.type || "text",
      is_published: !!row.is_published,
      translationsJson: JSON.stringify(row.translations || { en: "" }, null, 2),
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    if (openedFromUrl.current || loading || !highlightKey) return;
    const match = content.find((row) => row.key === highlightKey);
    if (match) {
      openedFromUrl.current = true;
      openEdit(match);
    }
  }, [content, highlightKey, loading]);

  const submitForm = async (e) => {
    e.preventDefault();
    let translations;
    try {
      translations = JSON.parse(form.translationsJson);
      if (!translations || typeof translations !== "object") {
        throw new Error("Translations must be a JSON object");
      }
    } catch (err) {
      toast({
        title: "Invalid JSON",
        description: err.message || "Could not parse translations",
        variant: "destructive",
      });
      return;
    }

    if (!form.key.trim()) {
      toast({
        title: "Validation",
        description: "Key is required",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      key: form.key.trim(),
      type: form.type,
      category: form.category.trim() || null,
      translations,
      is_published: form.is_published,
    };

    setSaving(true);
    try {
      if (editingKey) {
        await dispatch(
          updateContent({
            key: editingKey,
            data: {
              type: payload.type,
              category: payload.category,
              translations,
              is_published: payload.is_published,
            },
          })
        ).unwrap();
        toast({ title: "Updated" });
      } else {
        await dispatch(createContent(payload)).unwrap();
        toast({ title: "Created" });
      }
      setDialogOpen(false);
      dispatch(
        fetchContent(
          categoryFilter.trim() ? { category: categoryFilter.trim() } : {}
        )
      );
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Request failed",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete content key "${key}"?`)) return;
    try {
      await dispatch(deleteContent(key)).unwrap();
      toast({ title: "Deleted" });
      dispatch(
        fetchContent(
          categoryFilter.trim() ? { category: categoryFilter.trim() } : {}
        )
      );
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Delete failed",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">CMS content</h1>
          <p className="text-muted-foreground">
            Static copy keys (translations JSON). Admin JWT lists drafts too.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Add content
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Library</CardTitle>
          <CardDescription>
            Filter by category or search by content key
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <div className="relative max-w-xs flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by key…"
              value={keyFilter}
              onChange={(e) => {
                setKeyFilter(e.target.value);
                setHighlightKey("");
              }}
              className="pl-10"
            />
          </div>
          <Input
            placeholder="Category filter…"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="max-w-xs"
          />
          {keyFilter.trim() && (
            <Button type="button" variant="ghost" onClick={clearKeyFilter}>
              Clear key
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => dispatch(fetchContent({}))}
          >
            Refresh
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {loading && content.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContent.map((row) => (
                  <TableRow
                    key={row.key}
                    className={
                      highlightKey && row.key === highlightKey
                        ? "bg-primary/5"
                        : undefined
                    }
                  >
                    <TableCell className="font-mono text-xs max-w-[240px] truncate">
                      {row.key}
                    </TableCell>
                    <TableCell>{row.category || "—"}</TableCell>
                    <TableCell className="capitalize">{row.type}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          row.is_published !== false ? "default" : "secondary"
                        }
                      >
                        {row.is_published !== false ? "Published" : "Draft"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(row)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => remove(row.key)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && filteredContent.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              {content.length === 0
                ? "No content rows yet."
                : "No content matches your filters."}
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <form onSubmit={submitForm}>
            <DialogHeader>
              <DialogTitle>
                {editingKey ? "Edit content" : "New content"}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Key</Label>
                <Input
                  value={form.key}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, key: e.target.value }))
                  }
                  disabled={!!editingKey}
                  placeholder="page.hero.title"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label>Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value }))
                  }
                  placeholder="homepage"
                />
              </div>
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">text</SelectItem>
                    <SelectItem value="html">html</SelectItem>
                    <SelectItem value="markdown">markdown</SelectItem>
                    <SelectItem value="json">json</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.is_published}
                  onCheckedChange={(c) =>
                    setForm((f) => ({ ...f, is_published: c }))
                  }
                  id="pub"
                />
                <Label htmlFor="pub">Published</Label>
              </div>
              <div className="grid gap-2">
                <Label>Translations (JSON object)</Label>
                <Textarea
                  rows={10}
                  className="font-mono text-xs"
                  value={form.translationsJson}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      translationsJson: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ContentPage;
